import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  saveConfig, uploadAsset, clearAdminCreds, emailHoldingPromos, resetConfigSection,
  startHoldingPdf, waitHoldingJob, downloadHoldingJobFile,
} from "@/lib/ddfApi";
import { isHttpsUrl } from "@/lib/url";
import { isPromoExpired } from "@/lib/promoExpiry";

const EMPTY_PROMO_FORM = {
  name: "",
  category: "",
  description: "",
  header: "",
  priceAED: "",
  wasPrice: "",
  validUntil: "",
  terminal: "",
  terminalSpecific: false,
  imageName: "",
  ribbonColor: "#e63946",
  ribbonTextColor: "#ffffff",
  cornerTag: "",
  cornerTagPreset: "none",
  cornerTagColor: "#111827",
  cornerTagTextColor: "#ffffff",
  imageOnly: false,
};

/**
 * useAdminConfig — owns the entire admin editor state (draft config, upload
 * refs, promo form + holding state) and every mutation handler. Keeping this
 * out of the AdminPanel component keeps the panel a thin presentational shell.
 */
const promoOpt = (p) => ({
  id: p.id,
  name: p.name || "Image-only",
  category: p.category || "",
  status: p.status === "holding" ? "holding" : "",
  image: p.image || "",
  expired: !!p.validUntil && new Date(`${p.validUntil.slice(0, 10)}T23:59:59`) < new Date(),
});

export function useAdminConfig(config, onChanged, onClose) {
  const [draft, setDraft] = useState(() => structuredClone(config));
  const [saving, setSaving] = useState(false);

  // `structuredClone` is a global; `setDraft` is a stable React setter.
  // `config` (the only reactive value referenced) is correctly listed.
  useEffect(() => {
    setDraft(structuredClone(config));
  }, [config]);

  const persist = async (next) => {
    setSaving(true);
    try {
      await saveConfig(next);
      toast.success("Saved");
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onResetSection = async (section) => {
    setSaving(true);
    try {
      await resetConfigSection(section);
      toast.success(section === "style" ? "Style reset to original" : "Banner reset to original");
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const onScheduleRevert = (field) => (value) => {
    const next = { ...draft, [field]: value };
    setDraft(next);
    return persist(next);
  };

  // Pick up server-side auto-reverts while the panel stays open.
  useEffect(() => {
    const due = [
      config?.styleRevertAt,
      config?.bannerRevertAt,
      ...(config?.scheduledBanners || []).map((b) => b.goLiveAt),
    ].filter(Boolean);
    if (!due.length) return undefined;
    const dubaiNow = () => new Date(Date.now() + 4 * 3600 * 1000).toISOString().slice(0, 16);
    const t = setInterval(() => {
      if (due.some((v) => v <= dubaiNow())) {
        clearInterval(t);
        onChanged?.();
      }
    }, 15000);
    return () => clearInterval(t);
  }, [config?.styleRevertAt, config?.bannerRevertAt, config?.scheduledBanners, onChanged]);

  const upload = async (file) => uploadAsset(file);

  // --- Logo ---
  const logoFileRef = useRef();
  const onSaveLogo = async () => {
    const file = logoFileRef.current?.files?.[0];
    if (!file) {
      toast.error("Pick a logo image");
      return;
    }
    try {
      const asset = await upload(file);
      const next = { ...draft, siteLogo: { url: asset.url, type: asset.type } };
      setDraft(next);
      await persist(next);
      logoFileRef.current.value = "";
    } catch {
      toast.error("Upload failed");
    }
  };

  // --- Banner ---
  const bannerFileRef = useRef();
  const onSaveBanner = async () => {
    const file = bannerFileRef.current?.files?.[0];
    if (!file) {
      toast.error("Pick a file first");
      return;
    }
    try {
      const asset = await upload(file);
      const next = {
        ...draft,
        specialBanner: { url: asset.url, type: asset.type },
      };
      setDraft(next);
      await persist(next);
      bannerFileRef.current.value = "";
    } catch {
      toast.error("Upload failed");
    }
  };

  // --- Style ---
  const bgImgRef = useRef();
  const onSaveStyle = async () => {
    try {
      const next = { ...draft };
      next.styleSettings = { ...(next.styleSettings || {}) };
      if (bgImgRef.current?.files?.[0]) {
        const a = await upload(bgImgRef.current.files[0]);
        next.styleSettings.bgImage = a.url;
      }
      setDraft(next);
      await persist(next);
      if (bgImgRef.current) bgImgRef.current.value = "";
    } catch {
      toast.error("Upload failed");
    }
  };
  const onClearBgImage = async () => {
    const next = {
      ...draft,
      styleSettings: { ...(draft.styleSettings || {}), bgImage: "" },
    };
    setDraft(next);
    await persist(next);
    if (bgImgRef.current) bgImgRef.current.value = "";
  };

  // --- Ads ---
  const adFileRef = useRef();
  const EMPTY_AD_FORM = { name: "", linkPromoIds: [], linkUrl: "", publishAt: "", removeAt: "" };
  const [adForm, setAdForm] = useState(EMPTY_AD_FORM);
  const [adListTab, setAdListTab] = useState("holding");

  const genId = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `ad-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const onAddAd = async () => {
    const files = adFileRef.current?.files;
    if (!files || files.length === 0) {
      toast.error("Pick files first");
      return;
    }
    const linkUrl = (adForm.linkUrl || "").trim();
    if (linkUrl && !isHttpsUrl(linkUrl)) {
      toast.error("External URL must be a valid link starting with https://");
      return;
    }
    try {
      const newAds = [];
      for (const f of files) {
        const asset = await upload(f);
        newAds.push({
          id: genId(),
          url: asset.url,
          type: asset.type,
          name: (adForm.name || "").trim(),
          linkPromoIds: adForm.linkPromoIds || [],
          linkPromoId: "",
          linkUrl,
          status: "holding",
          publishAt: adForm.publishAt || "",
          removeAt: adForm.removeAt || "",
        });
      }
      const next = { ...draft, bannerAds: [...(draft.bannerAds || []), ...newAds] };
      setDraft(next);
      await persist(next);
      adFileRef.current.value = "";
      setAdForm(EMPTY_AD_FORM);
      toast.success("Ad(s) added to Holding");
      setAdListTab("holding");
    } catch {
      toast.error("Upload failed");
    }
  };

  const _adKey = (ad, idx) => ad.id || ad.url || `ad-${idx}`;

  const onUpdateAd = (key, patch, save = false) => {
    if (patch.linkUrl && !isHttpsUrl(patch.linkUrl)) {
      toast.error("External URL must be a valid link starting with https://");
      return;
    }
    const nextAds = (draft.bannerAds || []).map((ad, idx) =>
      _adKey(ad, idx) === key ? { ...ad, ...patch } : ad
    );
    const next = { ...draft, bannerAds: nextAds };
    setDraft(next);
    if (save) persist(next);
  };

  const onPublishAdNow = async (key) => {
    const nextAds = (draft.bannerAds || []).map((ad, idx) =>
      _adKey(ad, idx) === key ? { ...ad, status: "", publishAt: "" } : ad
    );
    const next = { ...draft, bannerAds: nextAds };
    setDraft(next);
    await persist(next);
    toast.success("Ad published");
  };

  const onMoveAdToHolding = async (key) => {
    const nextAds = (draft.bannerAds || []).map((ad, idx) =>
      _adKey(ad, idx) === key ? { ...ad, status: "holding" } : ad
    );
    const next = { ...draft, bannerAds: nextAds };
    setDraft(next);
    await persist(next);
  };

  const onDeleteAd = async (key) => {
    const nextAds = (draft.bannerAds || []).filter((ad, idx) => _adKey(ad, idx) !== key);
    const next = { ...draft, bannerAds: nextAds };
    setDraft(next);
    await persist(next);
  };

  // Flat list of promo products for the "link this ad to a promo" dropdown.
  const promoOptions = [
    ...(draft.departuresPromos || []).map((p) => ({ ...promoOpt(p), location: "Departures" })),
    ...(draft.arrivalsPromos || []).map((p) => ({ ...promoOpt(p), location: "Arrivals" })),
  ].filter((o) => o.id);

  // --- Maps ---
  const mapFileRef = useRef();
  const onAddMap = async () => {
    const file = mapFileRef.current?.files?.[0];
    if (!file) {
      toast.error("Pick a file first");
      return;
    }
    if (!file.type.includes("jpeg")) {
      toast.error("Only JPG/JPEG files allowed");
      return;
    }
    try {
      const asset = await upload(file);
      const next = {
        ...draft,
        shopMaps: [...(draft.shopMaps || []), { url: asset.url, type: "image" }],
      };
      setDraft(next);
      await persist(next);
      mapFileRef.current.value = "";
    } catch {
      toast.error("Upload failed");
    }
  };
  const onDeleteMap = async (idx) => {
    const next = {
      ...draft,
      shopMaps: draft.shopMaps.filter((_, i) => i !== idx),
    };
    setDraft(next);
    await persist(next);
  };

  // --- Prizes (images + links) ---
  const millRef = useRef();
  const luxRef = useRef();
  const motoRef = useRef();
  const onSavePrize = async () => {
    try {
      const next = { ...draft };
      if (millRef.current?.files?.[0]) {
        const a = await upload(millRef.current.files[0]);
        next.millenniumImg = a.url;
      }
      if (luxRef.current?.files?.[0]) {
        const a = await upload(luxRef.current.files[0]);
        next.luxuryCarImg = a.url;
      }
      if (motoRef.current?.files?.[0]) {
        const a = await upload(motoRef.current.files[0]);
        next.motorcycleImg = a.url;
      }
      setDraft(next);
      await persist(next);
      if (millRef.current) millRef.current.value = "";
      if (luxRef.current) luxRef.current.value = "";
      if (motoRef.current) motoRef.current.value = "";
    } catch {
      toast.error("Upload failed");
    }
  };

  // --- Register ---
  const regMediaRef = useRef();
  const onSaveRegister = async () => {
    try {
      let registerMedia = draft.registerMedia || { url: "", type: "image" };
      if (regMediaRef.current?.files?.[0]) {
        const a = await upload(regMediaRef.current.files[0]);
        registerMedia = { url: a.url, type: a.type };
      }
      const next = { ...draft, registerMedia };
      setDraft(next);
      await persist(next);
      if (regMediaRef.current) regMediaRef.current.value = "";
    } catch {
      toast.error("Upload failed");
    }
  };

  // --- Subscribe popup ---
  const subBgRef = useRef();
  const [subBgLocal, setSubBgLocal] = useState("");
  const onSubBgChange = (e) => {
    const f = e.target.files?.[0];
    setSubBgLocal(f ? URL.createObjectURL(f) : "");
  };
  const onResetSubscribe = async () => {
    if (!window.confirm("Reset to the original popup?")) return;
    const subscribeBgImage = { url: "", type: "image" };
    setDraft({ ...draft, subscribeBgImage });
    await persist({ ...draft, subscribeLink: config?.subscribeLink || "", subscribeBgImage });
    if (subBgRef.current) subBgRef.current.value = "";
    setSubBgLocal("");
  };
  const onSaveSubscribe = async (clearBg = false) => {
    const subscribeLink = (draft.subscribeLink || "").trim();
    if (!isHttpsUrl(subscribeLink)) {
      toast.error("Subscribe Link is required and must start with https://");
      return;
    }
    try {
      let subscribeBgImage = clearBg ? { url: "", type: "image" } : draft.subscribeBgImage || { url: "", type: "image" };
      if (!clearBg && subBgRef.current?.files?.[0]) {
        const a = await upload(subBgRef.current.files[0]);
        subscribeBgImage = { url: a.url, type: a.type };
      }
      const next = { ...draft, subscribeLink, subscribeBgImage };
      setDraft(next);
      await persist(next);
      if (subBgRef.current) subBgRef.current.value = "";
      setSubBgLocal("");
    } catch {
      toast.error("Upload failed");
    }
  };

  // --- Contact ---
  const onSaveContact = async () => persist(draft);

  // --- Promos ---
  const [promoLoc, setPromoLoc] = useState("departures");
  const [promoListTab, setPromoListTab] = useState("departures");
  const [promoForm, setPromoForm] = useState({ ...EMPTY_PROMO_FORM });
  const promoImgRef = useRef();
  const [editing, setEditing] = useState(null); // {loc, item}
  const editImgRef = useRef();
  const [batchPublishAt, setBatchPublishAt] = useState("");
  const [holdingEmail, setHoldingEmail] = useState("");
  const [emailingHolding, setEmailingHolding] = useState(false);
  const [downloadingHolding, setDownloadingHolding] = useState(false);
  const [holdingEmailError, setHoldingEmailError] = useState("");
  const [selectedHolding, setSelectedHolding] = useState([]);

  const toggleHoldingSelect = (id) =>
    setSelectedHolding((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );

  const onAddPromo = async () => {
    if (promoForm.imageOnly) {
      if (!promoImgRef.current?.files?.[0]) {
        toast.error("Please upload an image for an Image-Only promo");
        return;
      }
    } else if (!promoForm.name.trim() || !promoForm.category.trim()) {
      toast.error("Name and category required");
      return;
    }
    let image = "";
    let imageFileName = "";
    if (promoImgRef.current?.files?.[0]) {
      const a = await upload(promoImgRef.current.files[0]);
      image = a.url;
      imageFileName = promoImgRef.current.files[0].name;
    }
    const item = {
      id: crypto.randomUUID(),
      name: promoForm.name.trim(),
      category: promoForm.category.trim(),
      description: promoForm.description.trim(),
      header: promoForm.header.trim(),
      priceAED: parseFloat(promoForm.priceAED) || 0,
      wasPrice: parseFloat(promoForm.wasPrice) || 0,
      validUntil: promoForm.validUntil || "",
      terminal: (promoForm.terminal || "").trim(),
      ribbonColor: promoForm.ribbonColor || "",
      ribbonTextColor: promoForm.ribbonTextColor || "",
      cornerTag: (promoForm.cornerTag || "").trim(),
      cornerTagColor: promoForm.cornerTagColor || "",
      cornerTagTextColor: promoForm.cornerTagTextColor || "",
      // New promos land in the Holding panel — not live until published/scheduled.
      status: "holding",
      publishAt: "",
      imageOnly: !!promoForm.imageOnly,
      imageName: promoForm.imageOnly ? (promoForm.imageName || "").trim() : "",
      imageFileName,
      image,
    };
    // Image-only cards carry no textual details (category is kept for filtering
    // but is never shown on the card).
    if (item.imageOnly) {
      Object.assign(item, {
        name: "", description: "", header: "",
        priceAED: 0, wasPrice: 0, validUntil: "", terminal: "",
        ribbonColor: "", ribbonTextColor: "",
        cornerTag: "", cornerTagColor: "", cornerTagTextColor: "",
      });
    }
    const key = promoLoc === "departures" ? "departuresPromos" : "arrivalsPromos";
    const next = { ...draft, [key]: [...(draft[key] || []), item] };
    setDraft(next);
    await persist(next);
    // Newly added promos go to the Holding panel for scheduled publishing.
    setPromoListTab("holding");
    toast.success("Added to Holding — schedule or publish it to go live");
    setPromoForm({ ...EMPTY_PROMO_FORM });
    if (promoImgRef.current) promoImgRef.current.value = "";
  };

  const onRemovePromo = async (loc, id) => {
    const key = loc === "departures" ? "departuresPromos" : "arrivalsPromos";
    const next = { ...draft, [key]: (draft[key] || []).filter((p) => p.id !== id) };
    setDraft(next);
    await persist(next);
  };

  const onEditPromo = (loc, item) => {
    setEditing({ loc, item: { ...item } });
  };

  const onSaveEditing = async () => {
    if (!editing) return;
    let { loc, item } = editing;
    if (editImgRef.current?.files?.[0]) {
      const a = await upload(editImgRef.current.files[0]);
      item = { ...item, image: a.url, imageFileName: editImgRef.current.files[0].name };
    }
    item = { ...item, imageName: (item.imageName || "").trim(), terminal: (item.terminal || "").trim() };
    const key = loc === "departures" ? "departuresPromos" : "arrivalsPromos";
    const list = (draft[key] || []).map((p) => (p.id === item.id ? item : p));
    const next = { ...draft, [key]: list };
    setDraft(next);
    setEditing(null);
    await persist(next);
  };

  const onLogout = () => {
    clearAdminCreds();
    toast.success("Logged out");
    onClose?.();
  };

  // --- Holding panel: publish / schedule ---
  const patchPromoById = (base, loc, id, patch) => {
    const key = loc === "departures" ? "departuresPromos" : "arrivalsPromos";
    const list = (base[key] || []).map((p) => (p.id === id ? { ...p, ...patch } : p));
    return { ...base, [key]: list };
  };

  const onPublishNow = async (loc, id) => {
    const next = patchPromoById(draft, loc, id, { status: "published", publishAt: "" });
    setDraft(next);
    await persist(next);
  };

  const onChangePublishAt = (loc, id, value) => {
    setDraft(patchPromoById(draft, loc, id, { publishAt: value }));
  };

  const onScheduleItem = async (loc, id, value) => {
    if (!value) {
      toast.error("Pick a date & time first");
      return;
    }
    const next = patchPromoById(draft, loc, id, { publishAt: value, status: "holding" });
    setDraft(next);
    await persist(next);
    toast.success("Scheduled — will auto-publish at the set time");
  };

  const applyToAllHolding = (base, patchFn) => {
    const map = (list) =>
      (list || []).map((p) => (p.status === "holding" ? { ...p, ...patchFn(p) } : p));
    return {
      ...base,
      departuresPromos: map(base.departuresPromos),
      arrivalsPromos: map(base.arrivalsPromos),
    };
  };

  const onPublishAllNow = async () => {
    const next = applyToAllHolding(draft, () => ({ status: "published", publishAt: "" }));
    setDraft(next);
    await persist(next);
    toast.success("All holding promos published");
  };

  const onScheduleAll = async () => {
    if (!batchPublishAt) {
      toast.error("Pick a date & time first");
      return;
    }
    const next = applyToAllHolding(draft, () => ({ publishAt: batchPublishAt }));
    setDraft(next);
    await persist(next);
    toast.success("All holding promos scheduled");
  };

  // Expired promos (past Valid Until, Dubai time) always belong in Holding.
  const inHolding = (p) => p.status === "holding" || isPromoExpired(p);
  const holdingItems = [
    ...(draft.departuresPromos || [])
      .filter(inHolding)
      .map((p) => ({ ...p, loc: "departures" })),
    ...(draft.arrivalsPromos || [])
      .filter(inHolding)
      .map((p) => ({ ...p, loc: "arrivals" })),
  ];
  const publishedDepartures = (draft.departuresPromos || []).filter((p) => !inHolding(p));
  const publishedArrivals = (draft.arrivalsPromos || []).filter((p) => !inHolding(p));

  const onDuplicateHolding = async (location) => {
    if (selectedHolding.length === 0) {
      toast.error("Tick at least one promo to duplicate");
      return;
    }
    const key = location === "departures" ? "departuresPromos" : "arrivalsPromos";
    const sources = holdingItems.filter((p) => selectedHolding.includes(p.id));
    const copies = sources.map((p) => {
      const { loc, ...rest } = p;
      return { ...rest, id: crypto.randomUUID(), status: "holding", publishAt: "" };
    });
    const next = { ...draft, [key]: [...(draft[key] || []), ...copies] };
    setDraft(next);
    await persist(next);
    setSelectedHolding([]);
    toast.success(`Duplicated ${copies.length} promo${copies.length !== 1 ? "s" : ""} to ${location}`);
  };

  const onEmailHolding = async () => {
    const email = (holdingEmail || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (holdingItems.length === 0) {
      toast.error("No holding promo cards to send");
      return;
    }
    setEmailingHolding(true);
    setHoldingEmailError("");
    const tid = toast.loading(`Sending promo cards to ${email}…`);
    try {
      const { jobId } = await emailHoldingPromos(email, holdingItems);
      const job = await waitHoldingJob(jobId);
      if (job.status === "done") {
        toast.success(`Promo cards sent to ${email}${job.total > 1 ? ` in ${job.total} emails` : ""}.`, { id: tid });
        setHoldingEmail("");
      } else {
        const what = job.status === "partial"
          ? `Only ${job.sent} of ${job.total} emails were sent.`
          : "The email could not be sent.";
        const msg = `${what} ${job.error || ""}`.trim();
        toast.error(msg, { id: tid });
        setHoldingEmailError(msg);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to send email";
      toast.error(msg, { id: tid });
      setHoldingEmailError(msg);
    } finally {
      setEmailingHolding(false);
    }
  };

  const onDownloadHolding = async () => {
    if (holdingItems.length === 0) {
      toast.error("No holding promo cards to download");
      return;
    }
    setDownloadingHolding(true);
    const tid = toast.loading("Preparing the promo-card PDF…");
    try {
      const { jobId } = await startHoldingPdf(holdingItems);
      const job = await waitHoldingJob(jobId);
      if (job.status !== "done") throw new Error(job.error || "PDF could not be created");
      await downloadHoldingJobFile(job);
      toast.success(job.filename.endsWith(".zip") ? `Downloaded ZIP with ${job.total} PDFs.` : "PDF downloaded.", { id: tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Download failed", { id: tid });
    } finally {
      setDownloadingHolding(false);
    }
  };

  return {
    draft, setDraft, saving, persist,
    logoFileRef, onSaveLogo,
    bannerFileRef, onSaveBanner,
    bgImgRef, onSaveStyle, onClearBgImage,
    adFileRef, onAddAd, onDeleteAd,
    adForm, setAdForm, adListTab, setAdListTab,
    onUpdateAd, onPublishAdNow, onMoveAdToHolding, promoOptions,
    mapFileRef, onAddMap, onDeleteMap,
    millRef, luxRef, motoRef, onSavePrize,
    regMediaRef, onSaveRegister,
    subBgRef, onSaveSubscribe, subBgLocal, onSubBgChange, onResetSubscribe,
    onSaveContact,
    promoLoc, setPromoLoc, promoListTab, setPromoListTab,
    promoForm, setPromoForm, promoImgRef,
    editing, setEditing, editImgRef,
    batchPublishAt, setBatchPublishAt,
    holdingEmail, setHoldingEmail, emailingHolding, onEmailHolding,
    downloadingHolding, onDownloadHolding, holdingEmailError,
    onResetSection, onScheduleRevert,
    selectedHolding, toggleHoldingSelect, onDuplicateHolding,
    onAddPromo, onRemovePromo, onEditPromo, onSaveEditing,
    onLogout,
    onPublishNow, onChangePublishAt, onScheduleItem, onPublishAllNow, onScheduleAll,
    holdingItems, publishedDepartures, publishedArrivals,
  };
}
